import { useCallback, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export const captureElementAsPng = async (element, options = {}) => {
  if (!element) {
    throw new Error('Report element not found');
  }

  const {
    scale = 2,
    backgroundColor = '#ffffff',
    useCORS = true
  } = options;

  const canvas = await html2canvas(element, {
    scale,
    useCORS,
    logging: false,
    backgroundColor,
    width: element.scrollWidth,
    height: element.scrollHeight,
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight
  });

  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/png', 1);
  });

  if (!blob) {
    throw new Error('Failed to capture report as PNG');
  }

  return { canvas, blob };
};

const nextFrame = () => new Promise((resolve) => {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(resolve);
  });
});

const withTemporaryClass = async (element, className, callback) => {
  if (!className) {
    return callback();
  }

  element.classList.add(className);
  await nextFrame();

  try {
    return await callback();
  } finally {
    element.classList.remove(className);
  }
};

const drawPdfHeader = (pdf, {
  pageWidth,
  reportTitle,
  sectionTitle,
  subtitle,
  metadata = []
}) => {
  const accent = [255, 87, 51];
  const slate = [15, 23, 42];
  const muted = [100, 116, 139];
  const headerHeight = 18;
  const infoTop = 22;
  const infoHeight = metadata.length > 0 ? 18 : 10;

  pdf.setFillColor(slate[0], slate[1], slate[2]);
  pdf.rect(0, 0, pageWidth, headerHeight, 'F');
  pdf.setFillColor(accent[0], accent[1], accent[2]);
  pdf.rect(0, 0, 4, headerHeight, 'F');

  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.text(reportTitle, 12, 11);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text(sectionTitle, pageWidth - 12, 11, { align: 'right' });

  if (subtitle) {
    pdf.setTextColor(muted[0], muted[1], muted[2]);
    pdf.setFontSize(9);
    pdf.text(subtitle, 12, 18.2);
  }

  if (metadata.length > 0) {
    const pillWidth = Math.max(36, (pageWidth - 24 - ((metadata.length - 1) * 4)) / metadata.length);
    metadata.forEach((item, index) => {
      const x = 12 + (index * (pillWidth + 4));
      const y = infoTop;
      pdf.setFillColor(255, 248, 245);
      pdf.setDrawColor(255, 210, 191);
      pdf.roundedRect(x, y, pillWidth, infoHeight, 2, 2, 'FD');
      pdf.setTextColor(muted[0], muted[1], muted[2]);
      pdf.setFontSize(6.8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(String(item.label || '').toUpperCase(), x + 2.5, y + 5.5);
      pdf.setTextColor(slate[0], slate[1], slate[2]);
      pdf.setFontSize(8.5);
      pdf.setFont('helvetica', 'normal');
      const value = String(item.value || '');
      const wrapped = pdf.splitTextToSize(value, pillWidth - 5);
      pdf.text(wrapped, x + 2.5, y + 10);
    });
  }

  pdf.setDrawColor(226, 232, 240);
  pdf.line(10, infoTop + infoHeight + 4, pageWidth - 10, infoTop + infoHeight + 4);

  return infoTop + infoHeight + 6;
};

export const downloadElementAsPdf = async (element, options = {}) => {
  if (!element) {
    throw new Error('Report element not found');
  }

  const {
    filename = 'report.pdf',
    reportTitle = 'Report',
    sectionTitle = 'Section',
    subtitle = '',
    metadata = [],
    captureClassName = '',
    backgroundColor = '#ffffff',
    scale = 2
  } = options;

  const canvas = await withTemporaryClass(element, captureClassName, () => html2canvas(element, {
    scale,
    useCORS: true,
    logging: false,
    backgroundColor,
    width: element.scrollWidth,
    height: element.scrollHeight,
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight
  }));

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4', compress: true });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginX = 10;
  const footerHeight = 8;
  const bodyTop = drawPdfHeader(pdf, {
    pageWidth,
    reportTitle,
    sectionTitle,
    subtitle,
    metadata
  });
  const contentWidth = pageWidth - (marginX * 2);
  const imageWidth = contentWidth;
  const imageHeight = (canvas.height * imageWidth) / canvas.width;
  const bodyHeight = pageHeight - bodyTop - footerHeight - 8;
  const totalPages = Math.max(1, Math.ceil(imageHeight / bodyHeight));
  const imageData = canvas.toDataURL('image/png', 1.0);

  for (let pageIndex = 0; pageIndex < totalPages; pageIndex += 1) {
    if (pageIndex > 0) {
      pdf.addPage();
      drawPdfHeader(pdf, {
        pageWidth,
        reportTitle,
        sectionTitle,
        subtitle,
        metadata
      });
    }

    const imageY = bodyTop - (pageIndex * bodyHeight);
    pdf.addImage(imageData, 'PNG', marginX, imageY, imageWidth, imageHeight);

    pdf.setTextColor(100, 116, 139);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.text(`Page ${pageIndex + 1} of ${totalPages}`, pageWidth - 10, pageHeight - 5, { align: 'right' });
  }

  pdf.save(filename);
  return pdf;
};

export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const copyBlobToClipboard = async (blob, filename = 'report.png') => {
  if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
    return false;
  }

  await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
  return filename;
};

export const useReportPngExport = ({ targetRef, filenameBase = 'report' } = {}) => {
  const [exportingPng, setExportingPng] = useState(false);
  const [pngError, setPngError] = useState('');

  const copyReportAsPng = useCallback(async (filenameSuffix = '') => {
    const element = targetRef?.current;
    if (!element) {
      throw new Error('Report preview is not available');
    }

    setExportingPng(true);
    setPngError('');

    try {
      const { blob } = await captureElementAsPng(element);
      const filename = `${filenameBase}${filenameSuffix ? `-${filenameSuffix}` : ''}.png`;
      try {
        const copied = await copyBlobToClipboard(blob, filename);
        if (!copied) {
          downloadBlob(blob, filename);
        }
      } catch {
        downloadBlob(blob, filename);
      }

      return true;
    } catch (error) {
      setPngError(error.message || 'Failed to copy report as PNG');
      throw error;
    } finally {
      setExportingPng(false);
    }
  }, [filenameBase, targetRef]);

  const downloadReportAsPng = useCallback(async (filenameSuffix = '') => {
    const element = targetRef?.current;
    if (!element) {
      throw new Error('Report preview is not available');
    }

    setExportingPng(true);
    setPngError('');

    try {
      const { blob } = await captureElementAsPng(element);
      const filename = `${filenameBase}${filenameSuffix ? `-${filenameSuffix}` : ''}.png`;
      downloadBlob(blob, filename);
      return true;
    } catch (error) {
      setPngError(error.message || 'Failed to download report as PNG');
      throw error;
    } finally {
      setExportingPng(false);
    }
  }, [filenameBase, targetRef]);

  return {
    copyReportAsPng,
    downloadReportAsPng,
    exportingPng,
    pngError,
    setPngError
  };
};
